import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {
  AccountCircle as ProfileIcon,
  ArrowForward as ArrowForwardIcon,
  Badge as BadgeIcon,
  CheckCircle as CheckIcon,
  Email as EmailIcon,
  Event as EventsIcon,
  FindInPage as LostFoundIcon,
  Groups as ClubsIcon,
  Help as DoubtsIcon,
  Lightbulb as SkillsIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import LogoutNavbar from '../../components/LogoutNavbar';
import GlobalSidebar from '../../components/GlobalSidebar';
import PostLoginFooter from '../../components/PostLoginFooter';
import api from '../../api';
import { departmentOptions, yearOptions } from '../../data/academicOptions';
import '../../styles/AdminDashboard.css';
import '../../styles/AddMember.css';

const emptyFormData = {
  role: 'student',
  first_name: '',
  last_name: '',
  email: '',
  student_id: '',
  year: '',
  college: '',
  department: '',
};

const AddStudent = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [colleges, setColleges] = useState([]);
  const [loadingColleges, setLoadingColleges] = useState(true);
  const [formData, setFormData] = useState(emptyFormData);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [createdUser, setCreatedUser] = useState({ name: '', username: '' });
  const [menuWidths, setMenuWidths] = useState({
    year: null,
    college: null,
    department: null,
  });
  const [openSelectField, setOpenSelectField] = useState(null);
  const mainContentRef = useRef(null);

  const sidebarItems = [
    {
      label: 'Profile',
      path: '/admin/profile',
      action: () => navigate('/admin/profile'),
      icon: <ProfileIcon />,
    },
    {
      label: 'Manage Clubs',
      path: '/admin/clubs',
      action: () => navigate('/admin/clubs'),
      icon: <ClubsIcon />,
    },
    {
      label: 'Manage Students',
      path: '/admin/students',
      action: () => navigate('/admin/students'),
      icon: <SchoolIcon />,
    },
    {
      label: 'Manage Events',
      path: '/admin/events',
      action: () => navigate('/admin/events'),
      icon: <EventsIcon />,
    },
    {
      label: 'Lost and Found',
      path: '/lostfound',
      action: () => navigate('/lostfound'),
      icon: <LostFoundIcon />,
    },
    {
      label: 'Skills',
      path: '/skills',
      action: () => navigate('/skills'),
      icon: <SkillsIcon />,
    },
    {
      label: 'Doubts',
      path: '/yourdoubts',
      action: () => navigate('/yourdoubts'),
      icon: <DoubtsIcon />,
    },
    {
      label: 'Settings',
      path: '/settings',
      action: () => navigate('/settings'),
      icon: <SettingsIcon />,
    },
  ];

  useEffect(() => {
    const fetchColleges = async () => {
      try {
        setLoadingColleges(true);
        const response = await api.get('users/college_name/');
        setColleges(Array.isArray(response) ? response : []);
      } catch (error) {
        setColleges([]);
        setSubmitError('Failed to load colleges. Please try again later.');
      } finally {
        setLoadingColleges(false);
      }
    };

    fetchColleges();
  }, []);

  const validateField = (field, value) => {
    const nextErrors = { ...errors };

    switch (field) {
      case 'first_name':
        if (!value.trim()) nextErrors[field] = 'First name is required';
        else if (value.trim().length < 2) nextErrors[field] = 'First name must be at least 2 characters';
        else if (!/^[a-zA-Z\s]+$/.test(value.trim())) nextErrors[field] = 'Only letters and spaces';
        else delete nextErrors[field];
        break;
      case 'last_name':
        if (!value.trim()) nextErrors[field] = 'Last name is required';
        else if (value.trim().length < 2) nextErrors[field] = 'Last name must be at least 2 characters';
        else if (!/^[a-zA-Z\s]+$/.test(value.trim())) nextErrors[field] = 'Only letters and spaces';
        else delete nextErrors[field];
        break;
      case 'email': {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value) nextErrors[field] = 'Email is required';
        else if (!emailRegex.test(value)) nextErrors[field] = 'Enter a valid email address';
        else delete nextErrors[field];
        break;
      }
      case 'student_id':
        if (!value.trim()) nextErrors[field] = 'Student ID is required';
        else if (value.trim().length < 4) nextErrors[field] = 'Student ID must be at least 4 characters';
        else delete nextErrors[field];
        break;
      case 'year':
      case 'college':
      case 'department':
        if (!value) nextErrors[field] = 'This field is required';
        else delete nextErrors[field];
        break;
      default:
        break;
    }

    setErrors(nextErrors);
    return nextErrors;
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    const nextState = { ...formData, [field]: value };

    setFormData(nextState);
    setSubmitError('');

    if (touched[field]) {
      validateField(field, value);
    }
  };

  const handleBlur = (field) => (event) => {
    setTouched((current) => ({ ...current, [field]: true }));
    validateField(field, event.target.value);
  };

  const handleBack = () => {
    navigate('/admin/students');
  };

  const handleSelectOpen = (field) => (event) => {
    const width = event.currentTarget?.getBoundingClientRect().width;
    if (width) {
      setMenuWidths((current) => ({ ...current, [field]: width }));
    }
    setOpenSelectField(field);
  };

  const handleSelectClose = () => {
    setOpenSelectField(null);
  };

  const getMenuProps = (field) => {
    const width = menuWidths[field];
    return {
      disablePortal: false,
      disableScrollLock: true,
      container: mainContentRef.current,
      sx: {
        zIndex: 1300,
      },
      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
      transformOrigin: { vertical: 'top', horizontal: 'left' },
      marginThreshold: null,
      transitionDuration: 120,
      PaperProps: {
        sx: {
          mt: 0,
          width: width || undefined,
          minWidth: width || undefined,
          maxWidth: width || undefined,
        },
      },
    };
  };

  const validateAllFields = () => {
    const nextErrors = {};

    if (!formData.first_name.trim()) nextErrors.first_name = 'First name is required';
    else if (formData.first_name.trim().length < 2) nextErrors.first_name = 'First name must be at least 2 characters';
    else if (!/^[a-zA-Z\s]+$/.test(formData.first_name.trim())) nextErrors.first_name = 'Only letters and spaces';

    if (!formData.last_name.trim()) nextErrors.last_name = 'Last name is required';
    else if (formData.last_name.trim().length < 2) nextErrors.last_name = 'Last name must be at least 2 characters';
    else if (!/^[a-zA-Z\s]+$/.test(formData.last_name.trim())) nextErrors.last_name = 'Only letters and spaces';

    if (!formData.email) nextErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) nextErrors.email = 'Enter a valid email address';

    if (!formData.student_id.trim()) nextErrors.student_id = 'Student ID is required';
    else if (formData.student_id.trim().length < 4) nextErrors.student_id = 'Student ID must be at least 4 characters';

    if (!formData.year) nextErrors.year = 'This field is required';
    if (!formData.college) nextErrors.college = 'This field is required';
    if (!formData.department) nextErrors.department = 'This field is required';

    setErrors(nextErrors);
    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const allTouched = Object.keys(formData).reduce((accumulator, key) => {
      if (key !== 'role') {
        accumulator[key] = true;
      }
      return accumulator;
    }, {});
    setTouched(allTouched);

    const validationErrors = validateAllFields();
    if (Object.values(validationErrors).some(Boolean)) {
      setSubmitError('Please correct the highlighted fields before submitting.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const payload = {
        ...formData,
        email: formData.email.trim(),
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        student_id: formData.student_id.trim(),
        role: 'student',
      };

      const response = await api.post('users/register/', payload);
      const username = response?.user?.username || response?.username || '';
      const name = `${payload.first_name} ${payload.last_name}`.trim();
      const successMessage = `Student account created successfully for ${name}.`;

      setCreatedUser({ name, username });
      setIsSuccess(true);
      setFormData(emptyFormData);
      setTouched({});
      setErrors({});
      enqueueSnackbar(successMessage, { variant: 'success' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      const message =
        error?.email?.[0] ||
        error?.student_id?.[0] ||
        error?.college?.[0] ||
        error?.department?.[0] ||
        error?.year?.[0] ||
        error?.detail ||
        error?.error ||
        error?.message ||
        'Failed to create student account. Please try again.';
      setSubmitError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="admin-dashboard add-member add-student">
      <LogoutNavbar sidebarItems={isMobile ? sidebarItems : []} />
      <div className="dashboard-layout">
        {!isMobile && <GlobalSidebar />}
        <main
          className="dashboard-main"
          style={{
            marginLeft: isMobile ? 0 : undefined,
            width: isMobile ? '100%' : undefined,
          }}
        >
          <Container ref={mainContentRef} maxWidth="xl" className="dashboard-container">
            <header className="am-page-header">
              <Box className="am-header-left">
                <Box className="am-breadcrumb-row">
                  <Tooltip title="Back">
                    <IconButton onClick={handleBack} className="am-back-icon" size="medium" aria-label="Back">
                      <ArrowBackIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Breadcrumbs
                    className="am-breadcrumbs"
                    separator={<NavigateNextIcon fontSize="small" />}
                    aria-label="breadcrumb"
                  >
                    <Link
                      underline="hover"
                      color="inherit"
                      onClick={(clickEvent) => {
                        clickEvent.preventDefault();
                        navigate('/admin/dashboard');
                      }}
                      href="/admin/dashboard"
                      className="am-breadcrumb-link"
                    >
                      Dashboard
                    </Link>
                    <Link
                      underline="hover"
                      color="inherit"
                      onClick={(clickEvent) => {
                        clickEvent.preventDefault();
                        navigate('/admin/students');
                      }}
                      href="/admin/students"
                      className="am-breadcrumb-link"
                    >
                      Students
                    </Link>
                    <Typography color="text.primary" className="am-breadcrumb-current">
                      Add
                    </Typography>
                  </Breadcrumbs>
                </Box>

                <Box className="am-title-wrap">
                  <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" className="am-page-title">
                    Add Student
                  </Typography>
                  <Typography variant="body2" className="am-page-subtitle" color="text.secondary">
                    Create a student account using the admin workflow. The default password will be generated automatically.
                  </Typography>
                </Box>
              </Box>
            </header>

            <Paper className="am-card" elevation={0}>
              <Box className="am-card-header">
                <Box>
                  <Typography variant="h6" className="am-card-title">
                    Student registration
                  </Typography>
                  <Typography className="am-card-subtitle">
                    Complete personal and academic details in one flow.
                  </Typography>
                </Box>
              </Box>

              <Box className="am-card-body">
                {isSuccess ? (
                  <Box sx={{ textAlign: 'center', py: { xs: 2, md: 4 } }}>
                    <Avatar
                      sx={{
                        mx: 'auto',
                        mb: 2,
                        width: 72,
                        height: 72,
                        background: 'linear-gradient(135deg, #0D1C4F, #1E3A8A)',
                        boxShadow: '0 12px 30px rgba(13,28,79,.22)',
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 36, color: '#fff' }} />
                    </Avatar>

                    <Typography variant="h4" sx={{ fontWeight: 900, color: '#0f172a', mb: 1 }}>
                      Student account created successfully
                    </Typography>

                    {createdUser.name && (
                      <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 800, mb: 1 }}>
                        {createdUser.name}
                      </Typography>
                    )}

                    <Typography variant="body1" sx={{ color: '#64748b', mb: 3, maxWidth: 560, mx: 'auto' }}>
                      {createdUser.username
                        ? `The student account was created for ${createdUser.name}. Username: ${createdUser.username}. Login credentials are sent through the configured email flow.`
                        : `The student account was created for ${createdUser.name}. Login credentials are sent through the configured email flow.`}
                    </Typography>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={() => {
                          setIsSuccess(false);
                          setCreatedUser({ name: '', username: '' });
                        }}
                        className="am-secondary"
                      >
                        Add Another Student
                      </Button>
                      <Button
                        type="button"
                        variant="contained"
                        onClick={handleBack}
                        className="am-primary"
                        endIcon={<ArrowForwardIcon />}
                      >
                        Back to Students
                      </Button>
                    </Stack>
                  </Box>
                ) : (
                  <Box component="form" onSubmit={handleSubmit}>
                    <Stack spacing={4}>
                      {submitError && (
                        <Alert severity="error" variant="outlined">
                          {submitError}
                        </Alert>
                      )}

                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                          <Avatar sx={{ background: 'linear-gradient(135deg, #016f57 0%, #019a78 100%)' }}>
                            <PersonIcon />
                          </Avatar>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                            Personal Information
                          </Typography>
                        </Box>

                        <Grid container spacing={3}>
                          <Grid item xs={12} md={3}>
                            <TextField
                              label="First Name"
                              placeholder="Enter first name"
                              value={formData.first_name}
                              onChange={handleChange('first_name')}
                              onBlur={handleBlur('first_name')}
                              error={touched.first_name && !!errors.first_name}
                              helperText={touched.first_name && errors.first_name}
                              required
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <PersonIcon sx={{ color: '#94a3b8' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} md={3}>
                            <TextField
                              label="Last Name"
                              placeholder="Enter last name"
                              value={formData.last_name}
                              onChange={handleChange('last_name')}
                              onBlur={handleBlur('last_name')}
                              error={touched.last_name && !!errors.last_name}
                              helperText={touched.last_name && errors.last_name}
                              required
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <PersonIcon sx={{ color: '#94a3b8' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <TextField
                              label="Email Address"
                              placeholder="your.email@college.edu"
                              value={formData.email}
                              onChange={handleChange('email')}
                              onBlur={handleBlur('email')}
                              error={touched.email && !!errors.email}
                              helperText={touched.email && errors.email}
                              required
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <EmailIcon sx={{ color: '#94a3b8' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Box>

                      <Divider sx={{ borderColor: 'rgba(226,232,240,.8)' }} />

                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                          <Avatar sx={{ background: 'linear-gradient(135deg, #1E3A8A 0%, #8b5cf6 100%)' }}>
                            <SchoolIcon />
                          </Avatar>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                            Academic Details
                          </Typography>
                        </Box>

                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Student ID"
                              placeholder="Enter student ID"
                              value={formData.student_id}
                              onChange={handleChange('student_id')}
                              onBlur={handleBlur('student_id')}
                              error={touched.student_id && !!errors.student_id}
                              helperText={touched.student_id && errors.student_id}
                              required
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <BadgeIcon sx={{ color: '#94a3b8' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              select
                              label="Year of Study"
                              value={formData.year}
                              onChange={handleChange('year')}
                              onBlur={handleBlur('year')}
                              error={touched.year && !!errors.year}
                              helperText={touched.year && errors.year}
                              required
                              fullWidth
                              SelectProps={{
                                MenuProps: getMenuProps('year'),
                                onOpen: handleSelectOpen('year'),
                                onClose: handleSelectClose,
                                open: openSelectField === 'year',
                              }}
                            >
                              {yearOptions.map((year) => (
                                <MenuItem key={year} value={year}>
                                  {year}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              select
                              label="College"
                              value={formData.college}
                              onChange={handleChange('college')}
                              onBlur={handleBlur('college')}
                              error={touched.college && !!errors.college}
                              helperText={
                                touched.college && errors.college
                                  ? errors.college
                                  : loadingColleges
                                    ? 'Loading colleges…'
                                    : ' '
                              }
                              required
                              fullWidth
                              disabled={loadingColleges || colleges.length === 0}
                              SelectProps={{
                                MenuProps: getMenuProps('college'),
                                onOpen: handleSelectOpen('college'),
                                onClose: handleSelectClose,
                                open: openSelectField === 'college',
                              }}
                            >
                              {colleges.map((college) => (
                                <MenuItem key={college.id} value={college.id}>
                                  {college.name}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              select
                              label="Department"
                              value={formData.department}
                              onChange={handleChange('department')}
                              onBlur={handleBlur('department')}
                              error={touched.department && !!errors.department}
                              helperText={touched.department && errors.department}
                              required
                              fullWidth
                              SelectProps={{
                                MenuProps: getMenuProps('department'),
                                onOpen: handleSelectOpen('department'),
                                onClose: handleSelectClose,
                                open: openSelectField === 'department',
                              }}
                            >
                              {departmentOptions.map((department) => (
                                <MenuItem key={department} value={department}>
                                  {department}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        </Grid>
                      </Box>

                      <Divider sx={{ borderColor: 'rgba(226,232,240,.8)' }} />

                      <Box className="am-actions">
                        <Button
                          type="button"
                          variant="outlined"
                          onClick={handleBack}
                          className="am-secondary"
                          disabled={isSubmitting}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          className="am-primary"
                          disabled={isSubmitting || loadingColleges}
                          endIcon={!isSubmitting ? <ArrowForwardIcon /> : undefined}
                        >
                          {isSubmitting ? 'Creating Account…' : 'Create Student Account'}
                        </Button>
                      </Box>
                    </Stack>
                  </Box>
                )}
              </Box>
            </Paper>
          </Container>
        </main>
      </div>
      <PostLoginFooter />
    </div>
  );
};

export default AddStudent;
